import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { PageHeader, ContentCard, EmptyState, LazyLoadSentinel } from '../components/common';
import { ImageUploader, ImageGrid, ImageModal } from '../features/media-library';
import mediaService from '../services/media.service';
import { MediaImage } from '../types/media.types';
import { useSnackbar } from 'notistack';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

type SourceFilter = 'all' | 'facebook' | 'scraper' | 'uploaded';
type SortOption = 'newest' | 'oldest' | 'name' | 'size';

const ITEMS_PER_PAGE = 50;

export const MediaLibraryPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [images, setImages] = useState<MediaImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [selectedImage, setSelectedImage] = useState<MediaImage | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadImages = useCallback(async (page: number = 1, append: boolean = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const offset = (page - 1) * ITEMS_PER_PAGE;
      const response = await mediaService.getMediaLibrary({
        source: sourceFilter,
        search: searchQuery || undefined,
        limit: ITEMS_PER_PAGE,
        offset,
      });

      if (response.success) {
        setTotalCount(response.total);
        const sortedImages = [...response.images];

        // Client-side sorting
        sortedImages.sort((a, b) => {
          switch (sortBy) {
            case 'newest':
              return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
            case 'oldest':
              return new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime();
            case 'name':
              return a.name.localeCompare(b.name);
            case 'size':
              return (b.size || 0) - (a.size || 0);
            default:
              return 0;
          }
        });

        if (append) {
          setImages((prev) => [...prev, ...sortedImages]);
        } else {
          setImages(sortedImages);
        }

        setHasMore(response.hasMore);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error loading images:', error);
      enqueueSnackbar('Failed to load images', { variant: 'error' });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sourceFilter, searchQuery, sortBy, enqueueSnackbar]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadImages(currentPage + 1, true);
    }
  }, [currentPage, loadingMore, hasMore, loadImages]);

  const sentinelRef = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: loadingMore,
  });

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    loadImages(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter, sortBy]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
      setHasMore(true);
      loadImages(1, false);
    }, 500);

    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleImageClick = (image: MediaImage) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const handleUploadComplete = () => {
    enqueueSnackbar('Upload successful!', { variant: 'success' });
    setSourceFilter('uploaded');
    setCurrentPage(1);
    setHasMore(true);
    loadImages(1, false);
  };

  const handleUploadError = (error: string) => {
    enqueueSnackbar(error, { variant: 'error' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <PageHeader
            title="Media Library"
            subtitle="Manage your uploaded images and Facebook ad creatives"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading...' : `${images.length} / ${totalCount} images`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalCount} total files
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setCurrentPage(1);
              setHasMore(true);
              loadImages(1, false);
            }}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <ImageUploader
        onUploadComplete={handleUploadComplete}
        onUploadError={handleUploadError}
      />

      <ContentCard>
        <Tabs
          value={sourceFilter}
          onChange={(_, value) => setSourceFilter(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All Images" value="all" sx={{ textTransform: 'none' }} />
          <Tab label="Facebook Cached" value="facebook" sx={{ textTransform: 'none' }} />
          <Tab label="Scraped Images" value="scraper" sx={{ textTransform: 'none' }} />
          <Tab label="Uploaded Images" value="uploaded" sx={{ textTransform: 'none' }} />
        </Tabs>

        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.default',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search images by name, campaign, or ad..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                sx={{
                  backgroundColor: 'background.paper',
                }}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="name">By Name</MenuItem>
                <MenuItem value="size">By Size</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography color="text.secondary">Loading media library...</Typography>
              </Box>
            </Box>
          ) : images.length === 0 ? (
            <EmptyState
              message="No images found"
              icon="📷"
            />
          ) : (
            <>
              <ImageGrid images={images} onImageClick={handleImageClick} />
              <LazyLoadSentinel
                sentinelRef={sentinelRef}
                isLoading={loadingMore}
                hasMore={hasMore}
                loadingText="Loading more images..."
                endMessage="All images loaded"
              />
            </>
          )}
        </Box>
      </ContentCard>

      <ImageModal
        image={selectedImage}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default MediaLibraryPage;
